import { useState, useEffect, useCallback } from 'react';
import { BASE, authFetch } from '../constants';
import { useApp } from '../contexts/AppContext';

const API = BASE;

interface Connection {
  id: number;
  connection_name: string;
  erp_type: string;
  connection_status: string;
  org_id: number;
  [key: string]: any;
}

interface SFTPConfigData {
  host: string;
  port: number;
  username: string;
  password: string;
  remote_path: string;
  file_pattern: string;
  file_type_mapping: string;
}

interface PollHistoryEntry {
  polled_at: string;
  status: string;
  files_found: number;
  new_files: number;
  files_enqueued: number;
  duration_ms: number;
  errors: string | null;
}

interface PollResult {
  status: string;
  files_found?: number;
  new_files?: number;
  files_enqueued?: number;
  message?: string;
}

const statusBadge = (s: string) => {
  const colors: Record<string, string> = {
    SUCCESS: '#16a34a', COMPLETED: '#16a34a', ACTIVE: '#16a34a',
    FAILED: '#dc2626', ERROR: '#dc2626',
    RUNNING: '#2563eb', PROCESSING: '#2563eb',
    PENDING: '#ca8a04', INACTIVE: '#6b7280',
  };
  return (
    <span style={{
      background: colors[s?.toUpperCase()] || '#6b7280', color: 'var(--text-primary)', fontSize: 11,
      padding: '2px 8px', borderRadius: 4, fontWeight: 600,
    }}>{s}</span>
  );
};

const defaultConfig: SFTPConfigData = {
  host: '',
  port: 22,
  username: '',
  password: '',
  remote_path: '/',
  file_pattern: '*.csv',
  file_type_mapping: JSON.stringify({ "*.csv": "gl_upload", "*.xlsx": "subledger" }, null, 2),
};

export default function SFTPConfig() {
  const { authUser } = useApp();
  const [connections, setConnections] = useState<Connection[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [config, setConfig] = useState<SFTPConfigData>({ ...defaultConfig });
  const [pollHistory, setPollHistory] = useState<PollHistoryEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [polling, setPolling] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Fetch SFTP connections
  const fetchConnections = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authFetch(`${API}/api/v1/connectors?org_id=${authUser?.organization_id || 1}`);
      if (res.ok) {
        const data = await res.json();
        const items = (data.connections || data.items || data || []) as Connection[];
        const sftpConns = items.filter(
          (c: Connection) => c.connection_method === 'SFTP' || c.connection_name?.toLowerCase().includes('sftp')
        );
        setConnections(sftpConns);
      }
    } catch { /* ignore */ }
    setLoading(false);
  }, []);

  // Fetch config for selected connection
  const fetchConfig = useCallback(async (id: number) => {
    try {
      const res = await authFetch(`${API}/api/v1/connectors/sftp/${id}/config`);
      if (res.ok) {
        const data = await res.json();
        setConfig({
          host: data.host || '',
          port: data.port || 22,
          username: data.username || '',
          password: data.password || '',
          remote_path: data.remote_path || '/',
          file_pattern: data.file_pattern || '*.csv',
          file_type_mapping: typeof data.file_type_mapping === 'string'
            ? data.file_type_mapping
            : JSON.stringify(data.file_type_mapping || {}, null, 2),
        });
      } else {
        setConfig({ ...defaultConfig });
      }
    } catch {
      setConfig({ ...defaultConfig });
    }
  }, []);

  // Fetch poll history for selected connection
  const fetchPollHistory = useCallback(async (id: number) => {
    try {
      const res = await authFetch(`${API}/api/v1/connectors/sftp/${id}/poll-history`);
      if (res.ok) {
        const data = await res.json();
        setPollHistory(data.history || data.items || data || []);
      } else {
        setPollHistory([]);
      }
    } catch {
      setPollHistory([]);
    }
  }, []);

  useEffect(() => {
    fetchConnections();
  }, [fetchConnections]);

  useEffect(() => {
    if (selectedId) {
      fetchConfig(selectedId);
      fetchPollHistory(selectedId);
    } else {
      setConfig({ ...defaultConfig });
      setPollHistory([]);
    }
  }, [selectedId, fetchConfig, fetchPollHistory]);

  const showMessage = (type: 'success' | 'error', text: string) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 5000);
  };

  const handleSaveConfig = async () => {
    if (!selectedId) return;
    setSaving(true);
    try {
      let fileTypeMapping: any = config.file_type_mapping;
      try { fileTypeMapping = JSON.parse(config.file_type_mapping); } catch { /* send as-is */ }
      const res = await authFetch(`${API}/api/v1/connectors/sftp/${selectedId}/config`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          host: config.host,
          port: config.port,
          username: config.username,
          password: config.password,
          remote_path: config.remote_path,
          file_pattern: config.file_pattern,
          file_type_mapping: fileTypeMapping,
        }),
      });
      if (res.ok) {
        showMessage('success', 'Configuration saved successfully.');
      } else {
        const err = await res.json().catch(() => ({}));
        showMessage('error', err.detail || `Save failed (${res.status}).`);
      }
    } catch (e: any) {
      showMessage('error', e.message || 'Network error saving config.');
    }
    setSaving(false);
  };

  const handleTestConnection = async () => {
    if (!selectedId) return;
    setTesting(true);
    setMessage(null);
    try {
      const res = await authFetch(`${API}/api/v1/connectors/sftp/${selectedId}/test`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      if (res.ok) {
        const data = await res.json();
        showMessage('success', data.message || 'Connection test successful.');
      } else {
        const err = await res.json().catch(() => ({}));
        showMessage('error', err.detail || `Connection test failed (${res.status}).`);
      }
    } catch (e: any) {
      showMessage('error', e.message || 'Network error testing connection.');
    }
    setTesting(false);
  };

  const handlePollNow = async () => {
    if (!selectedId) return;
    setPolling(true);
    setMessage(null);
    try {
      const res = await authFetch(`${API}/api/v1/connectors/sftp/${selectedId}/poll`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      if (res.ok) {
        const data: PollResult = await res.json();
        const parts = [];
        if (data.files_found != null) parts.push(`${data.files_found} files found`);
        if (data.new_files != null) parts.push(`${data.new_files} new`);
        if (data.files_enqueued != null) parts.push(`${data.files_enqueued} enqueued`);
        showMessage('success', parts.length > 0 ? `Poll complete: ${parts.join(', ')}.` : (data.message || 'Poll completed.'));
        fetchPollHistory(selectedId);
      } else {
        const err = await res.json().catch(() => ({}));
        showMessage('error', err.detail || `Poll failed (${res.status}).`);
      }
    } catch (e: any) {
      showMessage('error', e.message || 'Network error during poll.');
    }
    setPolling(false);
  };

  const selectedConn = connections.find(c => c.id === selectedId);

  return (
    <div style={{ padding: 24 }}>
      <h2 style={{ margin: '0 0 16px', fontSize: 22, fontWeight: 700, color: 'var(--text-primary)' }}>
        SFTP Connections
      </h2>

      {/* Message banner */}
      {message && (
        <div style={{
          padding: '10px 16px', borderRadius: 6, marginBottom: 16, fontSize: 13, fontWeight: 600,
          background: message.type === 'success' ? '#14532d' : '#7f1d1d',
          color: message.type === 'success' ? 'var(--positive)' : '#fca5a5',
          border: `1px solid ${message.type === 'success' ? '#16a34a' : '#dc2626'}`,
        }}>
          {message.text}
        </div>
      )}

      {/* Connection selector */}
      <div style={{ marginBottom: 20 }}>
        <label style={{ fontSize: 13, color: '#94a3b8', display: 'block', marginBottom: 6 }}>
          Select Connection
        </label>
        {loading ? (
          <div style={{ color: '#94a3b8', fontSize: 13 }}>Loading connections...</div>
        ) : connections.length === 0 ? (
          <div style={{ color: '#64748b', fontSize: 13 }}>
            No SFTP connections found. Create one via the ERP Connectors API (POST /api/v1/connectors).
          </div>
        ) : (
          <select
            value={selectedId ?? ''}
            onChange={(e) => setSelectedId(e.target.value ? Number(e.target.value) : null)}
            style={{
              background: 'var(--bg-card)', color: 'var(--text-primary)', border: '1px solid var(--border-default)',
              borderRadius: 6, padding: '8px 12px', fontSize: 13, minWidth: 300,
              cursor: 'pointer',
            }}
          >
            <option value="">-- Select an SFTP connection --</option>
            {connections.map(c => (
              <option key={c.id} value={c.id}>
                {c.connection_name} (ID: {c.id}) - {c.connection_status || 'UNKNOWN'}
              </option>
            ))}
          </select>
        )}
      </div>

      {/* Config form + actions */}
      {selectedId && selectedConn && (
        <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', alignItems: 'flex-start' }}>
          {/* Config form card */}
          <div style={{
            background: 'var(--bg-card)', borderRadius: 8, padding: 20, flex: '1 1 420px', maxWidth: 560,
          }}>
            <h3 style={{ margin: '0 0 16px', fontSize: 16, fontWeight: 700, color: 'var(--text-primary)' }}>
              Configuration: {selectedConn.connection_name}
            </h3>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {/* Host */}
              <div>
                <label style={labelStyle}>Host</label>
                <input
                  type="text"
                  value={config.host}
                  onChange={e => setConfig(c => ({ ...c, host: e.target.value }))}
                  placeholder="sftp.example.com"
                  style={inputStyle}
                />
              </div>

              {/* Port */}
              <div>
                <label style={labelStyle}>Port</label>
                <input
                  type="number"
                  value={config.port}
                  onChange={e => setConfig(c => ({ ...c, port: Number(e.target.value) || 22 }))}
                  style={{ ...inputStyle, maxWidth: 120 }}
                />
              </div>

              {/* Username */}
              <div>
                <label style={labelStyle}>Username</label>
                <input
                  type="text"
                  value={config.username}
                  onChange={e => setConfig(c => ({ ...c, username: e.target.value }))}
                  placeholder="sftp_user"
                  style={inputStyle}
                />
              </div>

              {/* Password */}
              <div>
                <label style={labelStyle}>Password</label>
                <input
                  type="password"
                  value={config.password}
                  onChange={e => setConfig(c => ({ ...c, password: e.target.value }))}
                  placeholder="********"
                  style={inputStyle}
                />
              </div>

              {/* Remote Path */}
              <div>
                <label style={labelStyle}>Remote Path</label>
                <input
                  type="text"
                  value={config.remote_path}
                  onChange={e => setConfig(c => ({ ...c, remote_path: e.target.value }))}
                  placeholder="/uploads/gl"
                  style={inputStyle}
                />
              </div>

              {/* File Pattern */}
              <div>
                <label style={labelStyle}>File Pattern</label>
                <input
                  type="text"
                  value={config.file_pattern}
                  onChange={e => setConfig(c => ({ ...c, file_pattern: e.target.value }))}
                  placeholder="*.csv"
                  style={inputStyle}
                />
              </div>

              {/* File Type Mapping */}
              <div>
                <label style={labelStyle}>File Type Mapping (JSON)</label>
                <textarea
                  value={config.file_type_mapping}
                  onChange={e => setConfig(c => ({ ...c, file_type_mapping: e.target.value }))}
                  rows={4}
                  style={{
                    ...inputStyle,
                    fontFamily: 'monospace',
                    resize: 'vertical',
                    minHeight: 80,
                  }}
                />
              </div>
            </div>

            {/* Action buttons */}
            <div style={{ display: 'flex', gap: 8, marginTop: 20, flexWrap: 'wrap' }}>
              <button
                onClick={handleSaveConfig}
                disabled={saving}
                style={{
                  ...btnStyle,
                  background: 'var(--navy-700)',
                  opacity: saving ? 0.6 : 1,
                }}
              >
                {saving ? 'Saving...' : 'Save Config'}
              </button>
              <button
                onClick={handleTestConnection}
                disabled={testing}
                style={{
                  ...btnStyle,
                  background: 'var(--navy-700)',
                  opacity: testing ? 0.6 : 1,
                }}
              >
                {testing ? 'Testing...' : 'Test Connection'}
              </button>
              <button
                onClick={handlePollNow}
                disabled={polling}
                style={{
                  ...btnStyle,
                  background: 'var(--navy-800)',
                  opacity: polling ? 0.6 : 1,
                }}
              >
                {polling ? 'Polling...' : 'Poll Now'}
              </button>
            </div>
          </div>

          {/* Connection info card */}
          <div style={{
            background: 'var(--bg-card)', borderRadius: 8, padding: 20, flex: '0 1 280px',
          }}>
            <h3 style={{ margin: '0 0 12px', fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>
              Connection Details
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, fontSize: 13 }}>
              <div>
                <span style={{ color: '#94a3b8' }}>ID: </span>
                <span style={{ color: 'var(--text-primary)' }}>{selectedConn.id}</span>
              </div>
              <div>
                <span style={{ color: '#94a3b8' }}>Name: </span>
                <span style={{ color: 'var(--text-primary)' }}>{selectedConn.connection_name}</span>
              </div>
              <div>
                <span style={{ color: '#94a3b8' }}>Type: </span>
                <span style={{ color: 'var(--text-primary)' }}>{selectedConn.erp_type}</span>
              </div>
              <div>
                <span style={{ color: '#94a3b8' }}>Status: </span>
                {statusBadge(selectedConn.connection_status || 'UNKNOWN')}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Poll History table */}
      {selectedId && (
        <div style={{ marginTop: 32 }}>
          <h3 style={{ margin: '0 0 12px', fontSize: 16, fontWeight: 700, color: 'var(--text-primary)' }}>
            Poll History
          </h3>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #334155' }}>
                {['Polled At', 'Status', 'Files Found', 'New Files', 'Enqueued', 'Duration (ms)', 'Errors'].map(h => (
                  <th key={h} style={{ padding: '8px 10px', textAlign: 'left', color: '#94a3b8', fontWeight: 600 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {pollHistory.map((row, idx) => (
                <tr key={idx} style={{ borderBottom: '1px solid #1e293b' }}>
                  <td style={{ padding: '6px 10px', fontSize: 11 }}>
                    {row.polled_at ? new Date(row.polled_at).toLocaleString() : '-'}
                  </td>
                  <td style={{ padding: '6px 10px' }}>{statusBadge(row.status)}</td>
                  <td style={{ padding: '6px 10px', textAlign: 'right' }}>{row.files_found ?? '-'}</td>
                  <td style={{ padding: '6px 10px', textAlign: 'right' }}>{row.new_files ?? '-'}</td>
                  <td style={{ padding: '6px 10px', textAlign: 'right' }}>{row.files_enqueued ?? '-'}</td>
                  <td style={{ padding: '6px 10px', textAlign: 'right' }}>{row.duration_ms ?? '-'}</td>
                  <td style={{ padding: '6px 10px', color: row.errors ? '#fca5a5' : '#64748b', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {row.errors || '-'}
                  </td>
                </tr>
              ))}
              {pollHistory.length === 0 && (
                <tr>
                  <td colSpan={7} style={{ padding: 20, textAlign: 'center', color: '#64748b' }}>
                    No poll history available.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* Shared inline styles */
const labelStyle: React.CSSProperties = {
  fontSize: 12, color: '#94a3b8', display: 'block', marginBottom: 4, fontWeight: 600,
};

const inputStyle: React.CSSProperties = {
  width: '100%', background: 'var(--bg-card)', color: 'var(--text-primary)', border: '1px solid var(--border-default)',
  borderRadius: 6, padding: '8px 12px', fontSize: 13, boxSizing: 'border-box',
};

const btnStyle: React.CSSProperties = {
  padding: '8px 20px', borderRadius: 6, border: 'none', cursor: 'pointer',
  fontWeight: 600, fontSize: 13, color: 'var(--text-primary)',
};
